<template>
  <Teleport to="body">
    <div v-if="isOpen" class="health-evolution-overlay" @click="close"></div>

    <aside v-if="isOpen" class="health-evolution-drawer" :class="{ 'health-evolution-drawer--pediatric': isPediatricMode }" @click.stop>
      <header class="health-evolution-header">
        <div>
          <h3 class="health-evolution-title">
            <i class="bi bi-graph-up-arrow text-primary me-2"></i>
            Evolução — {{ groupName }}
          </h3>
          <p class="health-evolution-subtitle">
            <template v-if="isPediatricMode && growthChartData?.patient?.gender_label">
              {{ growthChartData.patient.gender_label }} · {{ growthChartData.reference_standard }}
            </template>
            <template v-else>
              {{ filteredRecords.length }} registro{{ filteredRecords.length === 1 ? '' : 's' }} no período
            </template>
          </p>
        </div>
        <button type="button" class="btn btn-sm btn-icon btn-light" @click="close">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="health-evolution-toolbar">
        <div class="health-evolution-periods">
          <button
            v-for="option in periodOptions"
            :key="option.value"
            type="button"
            class="btn btn-sm"
            :class="period === option.value ? 'btn-primary' : 'btn-light'"
            @click="period = option.value"
          >
            {{ option.label }}
          </button>
        </div>
      </div>

      <div class="health-evolution-body">
        <div v-if="isPediatricMode && isLoadingGrowthCharts" class="health-evolution-empty">
          <div class="spinner-border text-primary" role="status"></div>
          <p class="mt-3 mb-0">Carregando curvas de crescimento...</p>
        </div>

        <div v-else-if="!hasData" class="health-evolution-empty">
          <i class="bi bi-bar-chart-line fs-1 text-muted"></i>
          <p class="mt-3 mb-0">Nenhum registro encontrado para análise neste período.</p>
        </div>

        <template v-else>
          <div v-if="growthChartError" class="health-evolution-alert">
            <i class="bi bi-exclamation-triangle text-warning me-2"></i>
            {{ growthChartError }}
          </div>

          <section v-if="displaySummaryCards.length > 0" class="health-evolution-section">
            <h4 class="health-evolution-section-title">{{ isPediatricMode ? 'Resumo (percentil OMS)' : 'Resumo atual' }}</h4>
            <div class="health-evolution-summary-grid">
              <div
                v-for="card in displaySummaryCards"
                :key="card.id"
                class="health-evolution-summary-card"
                :class="`health-evolution-summary-card--${card.status}`"
              >
                <span class="health-evolution-summary-label">{{ card.name }}</span>
                <span class="health-evolution-summary-value">
                  {{ formatHealthIndicatorValue(card.latestValue, card) }}
                </span>
                <div v-if="isPediatricMode && card.percentile !== undefined" class="health-evolution-summary-percentile">
                  <span class="badge" :class="percentileBadgeClass(card.percentile)">
                    {{ formatPercentile(card.percentile) }}
                  </span>
                  <span class="text-muted">Z {{ card.zScore }}</span>
                </div>
                <div v-else-if="card.delta !== null" class="health-evolution-summary-delta">
                  <i :class="trendIcon(card.trend)"></i>
                  <span>{{ formatDelta(card.delta, card.unit) }} vs. anterior</span>
                </div>
                <span v-if="card.latestDate" class="health-evolution-summary-date">
                  {{ formatDate(card.latestDate) }}
                </span>
                <span v-else-if="isPediatricMode && card.ageMonths !== undefined" class="health-evolution-summary-date">
                  {{ card.ageMonths }} meses de idade
                </span>
              </div>
            </div>
          </section>

          <template v-if="isPediatricMode">
            <section
              v-for="chart in growthCharts"
              :key="chart.indicator_id"
              class="health-evolution-section"
            >
              <h4 class="health-evolution-section-title">{{ chart.metric_label }}</h4>
              <PediatricGrowthChart
                :chart="chart"
                :reference-standard="growthChartData?.reference_standard"
              />
            </section>
          </template>

          <template v-else>
          <section
            v-for="chartGroup in chartGroups"
            :key="chartGroup.key"
            class="health-evolution-section"
          >
            <h4 class="health-evolution-section-title">{{ chartGroup.label }}</h4>
            <div class="health-evolution-chart-card">
              <apexchart
                v-if="chartOptionsByGroup[chartGroup.key]"
                type="line"
                height="260"
                :options="chartOptionsByGroup[chartGroup.key]"
                :series="chartSeriesByGroup[chartGroup.key]"
              />
            </div>
          </section>
          </template>

          <section v-if="tableRecords.length > 0" class="health-evolution-section">
            <h4 class="health-evolution-section-title">Histórico detalhado</h4>
            <div class="table-responsive health-evolution-table-wrap">
              <table class="table table-row-bordered table-row-gray-200 align-middle gs-0 gy-3 mb-0">
                <thead>
                  <tr class="fw-bold text-muted">
                    <th>Data</th>
                    <th v-if="isPediatricMode">Idade</th>
                    <th v-for="indicator in healthIndicators" :key="indicator.id_health_indicator">
                      {{ indicator.health_indicator_name }}
                    </th>
                    <th v-if="isPediatricMode">Percentil</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="record in tableRecords" :key="record.id_patient_health_data">
                    <td class="text-nowrap fw-semibold">{{ formatDateTime(record.created_at) }}</td>
                    <td v-if="isPediatricMode" class="text-nowrap">
                      {{ formatRecordAge(record) }}
                    </td>
                    <td v-for="indicator in healthIndicators" :key="indicator.id_health_indicator">
                      {{ formatCellValue(record, indicator) }}
                    </td>
                    <td v-if="isPediatricMode">
                      {{ formatRecordPercentile(record) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </template>
      </div>
    </aside>
  </Teleport>
</template>

<script>
import moment from 'moment';
import VueApexCharts from 'vue3-apexcharts/src/vue3-apexcharts';
import http from '../../../helpers/http';
import PediatricGrowthChart from './PediatricGrowthChart.vue';
import {
  PERIOD_OPTIONS,
  CHART_GROUP_COLORS,
  buildChartGroups,
  buildChartSeries,
  buildReferenceAnnotations,
  buildSummaryCards,
  filterRecordsByPeriod,
  formatDelta,
  formatValue,
  formatHealthIndicatorValue,
  getIndicatorValue,
  sortRecordsNewestFirst,
} from '../../../helpers/healthDataEvolution';
import {
  buildPediatricSummaryCards,
  formatPercentile,
  isPediatricGrowthMode,
} from '../../../helpers/pediatricGrowthChart';

export default {
  name: 'HealthDataEvolutionDrawer',
  components: {
    apexchart: VueApexCharts,
    PediatricGrowthChart,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    groupName: {
      type: String,
      default: '',
    },
    healthIndicators: {
      type: Array,
      default: () => [],
    },
    patientHealthData: {
      type: Array,
      default: () => [],
    },
    professionalId: {
      type: [Number, String],
      default: null,
    },
    patientId: {
      type: [Number, String],
      default: null,
    },
    healthIndicatorGroupId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ['close'],
  data() {
    return {
      period: 'all',
      periodOptions: PERIOD_OPTIONS,
      growthChartData: null,
      isLoadingGrowthCharts: false,
      growthChartError: null,
    };
  },
  computed: {
    isPediatricMode() {
      return isPediatricGrowthMode(this.healthIndicators);
    },
    filteredRecords() {
      return filterRecordsByPeriod(this.patientHealthData, this.period);
    },
    hasData() {
      if (this.isPediatricMode) {
        return (this.growthCharts.length > 0) || this.filteredRecords.length > 0;
      }

      return this.filteredRecords.length > 0;
    },
    summaryCards() {
      return buildSummaryCards(this.filteredRecords, this.healthIndicators);
    },
    pediatricSummaryCards() {
      return buildPediatricSummaryCards(this.growthCharts);
    },
    displaySummaryCards() {
      return this.isPediatricMode ? this.pediatricSummaryCards : this.summaryCards;
    },
    growthCharts() {
      return this.growthChartData?.charts ?? [];
    },
    chartGroups() {
      return buildChartGroups(this.healthIndicators);
    },
    tableRecords() {
      return sortRecordsNewestFirst(this.filteredRecords);
    },
    chartSeriesByGroup() {
      const result = {};

      this.chartGroups.forEach((group) => {
        const { series } = buildChartSeries(this.filteredRecords, group.indicators);
        result[group.key] = series.map((item) => ({
          name: item.name,
          data: item.data,
        }));
      });

      return result;
    },
    chartOptionsByGroup() {
      const result = {};

      this.chartGroups.forEach((group) => {
        const { categories, series } = buildChartSeries(this.filteredRecords, group.indicators);
        const colors = CHART_GROUP_COLORS[group.key]
          ?? series.map((item) => item.color);

        result[group.key] = {
          chart: {
            type: 'line',
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: 'inherit',
          },
          colors,
          stroke: {
            curve: 'smooth',
            width: 3,
          },
          markers: {
            size: 5,
            strokeWidth: 2,
            hover: { size: 7 },
          },
          dataLabels: { enabled: false },
          legend: {
            position: 'top',
            horizontalAlign: 'right',
            fontSize: '12px',
          },
          grid: {
            borderColor: '#eff2f5',
            strokeDashArray: 4,
          },
          xaxis: {
            categories,
            labels: {
              style: { fontSize: '11px', colors: '#7e8299' },
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          yaxis: {
            labels: {
              style: { fontSize: '11px', colors: '#7e8299' },
              formatter: (value) => (value !== null && value !== undefined ? Math.round(value * 10) / 10 : ''),
            },
          },
          tooltip: {
            shared: true,
            intersect: false,
            y: {
              formatter: (value, { seriesIndex }) => {
                const unit = series[seriesIndex]?.unit ?? '';
                if (value === null || value === undefined) {
                  return '—';
                }
                return unit ? `${value} ${unit}` : `${value}`;
              },
            },
          },
          annotations: {
            yaxis: buildReferenceAnnotations(group.indicators),
          },
        };
      });

      return result;
    },
  },
  watch: {
    isOpen(open) {
      if (open) {
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.handleKeydown);
        this.loadGrowthCharts();
      } else {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.handleKeydown);
        this.growthChartData = null;
        this.growthChartError = null;
      }
    },
    period() {
      if (this.isOpen && this.isPediatricMode) {
        this.loadGrowthCharts();
      }
    },
  },
  beforeUnmount() {
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    formatValue,
    formatHealthIndicatorValue,
    formatDelta,
    formatPercentile,
    close() {
      this.$emit('close');
    },
    loadGrowthCharts() {
      if (!this.isPediatricMode || !this.professionalId || !this.patientId || !this.healthIndicatorGroupId) {
        return;
      }

      this.isLoadingGrowthCharts = true;
      this.growthChartError = null;

      http.get(`/professionals/${this.professionalId}/patients/${this.patientId}/health-data/growth-charts`, {
        params: {
          id_health_indicator_group: this.healthIndicatorGroupId,
          period: this.period,
        },
      }).then((response) => {
        if (response.data?.error) {
          this.growthChartData = null;
          this.growthChartError = response.data.message;
          return;
        }

        this.growthChartData = response.data;
      }).catch((error) => {
        this.growthChartData = null;
        this.growthChartError = error.response?.data?.message ?? 'Não foi possível carregar as curvas de crescimento.';
      }).finally(() => {
        this.isLoadingGrowthCharts = false;
      });
    },
    percentileBadgeClass(percentile) {
      if (percentile >= 3 && percentile <= 97) {
        return 'badge-light-success';
      }

      if (percentile < 3) {
        return 'badge-light-warning';
      }

      return 'badge-light-danger';
    },
    formatRecordAge(record) {
      const measurement = this.findPrimaryMeasurement(record);

      return measurement ? `${measurement.age_months} m` : '—';
    },
    formatRecordPercentile(record) {
      const measurement = this.findPrimaryMeasurement(record);

      return measurement ? formatPercentile(measurement.percentile) : '—';
    },
    findPrimaryMeasurement(record) {
      const weightChart = this.growthCharts.find((chart) => chart.indicator_code === 'PESO');

      if (!weightChart) {
        return null;
      }

      const date = moment(record.created_at).format('YYYY-MM-DD');

      return (weightChart.measurements ?? []).find((item) => item.date === date) ?? null;
    },
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
      }
    },
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY');
    },
    formatDateTime(date) {
      return moment(date).format('DD/MM/YYYY HH:mm');
    },
    formatCellValue(record, indicator) {
      const value = getIndicatorValue(record, indicator.id_health_indicator);

      return formatHealthIndicatorValue(value, indicator);
    },
    trendIcon(trend) {
      if (trend === 'up') {
        return 'bi bi-arrow-up-short text-danger';
      }
      if (trend === 'down') {
        return 'bi bi-arrow-down-short text-primary';
      }
      return 'bi bi-dash-lg text-muted';
    },
  },
};
</script>

<style scoped>
.health-evolution-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  z-index: 1060;
  backdrop-filter: blur(2px);
}

.health-evolution-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: min(680px, 100vw);
  height: 100vh;
  background: #fff;
  z-index: 1061;
  display: flex;
  flex-direction: column;
  box-shadow: -12px 0 40px rgba(15, 23, 42, 0.15);
}

.health-evolution-drawer--pediatric {
  width: min(760px, 100vw);
}

.health-evolution-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #eff2f5;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
}

.health-evolution-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #181c32;
}

.health-evolution-subtitle {
  margin: 0.35rem 0 0;
  font-size: 0.8rem;
  color: #7e8299;
}

.health-evolution-toolbar {
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid #eff2f5;
  background: #fafbfc;
}

.health-evolution-periods {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.health-evolution-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem 1.5rem;
}

.health-evolution-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 280px;
  color: #7e8299;
}

.health-evolution-section {
  margin-bottom: 1.5rem;
}

.health-evolution-section-title {
  margin: 0 0 0.75rem;
  font-size: 0.9rem;
  font-weight: 700;
  color: #3f4254;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.health-evolution-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.75rem;
}

.health-evolution-summary-card {
  background: #f9fafb;
  border: 1px solid #eff2f5;
  border-radius: 0.75rem;
  padding: 1rem;
  border-left: 4px solid #009ef7;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.health-evolution-summary-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.health-evolution-summary-card--normal {
  border-left-color: #50cd89;
}

.health-evolution-summary-card--high {
  border-left-color: #f1416c;
}

.health-evolution-summary-card--low {
  border-left-color: #ffc700;
}

.health-evolution-summary-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #7e8299;
  margin-bottom: 0.35rem;
}

.health-evolution-summary-value {
  display: block;
  font-size: 1.35rem;
  font-weight: 700;
  color: #181c32;
  line-height: 1.2;
}

.health-evolution-summary-delta {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.5rem;
  font-size: 0.78rem;
  color: #5e6278;
}

.health-evolution-alert {
  display: flex;
  align-items: center;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 0.75rem;
  background: #fff8dd;
  border: 1px solid #ffe2a8;
  color: #7e6a35;
  font-size: 0.85rem;
}

.health-evolution-summary-percentile {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.78rem;
}

.health-evolution-summary-date {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.72rem;
  color: #a1a5b7;
}

.health-evolution-chart-card {
  background: #fff;
  border: 1px solid #eff2f5;
  border-radius: 0.75rem;
  padding: 0.75rem 0.5rem 0.25rem;
}

.health-evolution-table-wrap {
  border: 1px solid #eff2f5;
  border-radius: 0.75rem;
  max-height: 320px;
  overflow: auto;
}

.health-evolution-table-wrap thead th {
  position: sticky;
  top: 0;
  background: #f9fafb;
  z-index: 1;
  font-size: 0.78rem;
  white-space: nowrap;
}

.health-evolution-table-wrap tbody td {
  font-size: 0.82rem;
}

@media (max-width: 576px) {
  .health-evolution-drawer {
    width: 100vw;
  }

  .health-evolution-summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
